import { XIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { classNames } from "../../utils"

interface PopUpProps {
  id: string
  children: React.ReactNode
  slideInAfter?: number // Seconds to wait before showing the popup (default: 3 seconds)
}

const PopUpNotification: React.FC<PopUpProps> = ({
  id,
  children,
  slideInAfter = 3
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const localStorageKey = `popup-${id}-closed`
  const [wasClosed, setWasClosed] = useState(
    localStorage.getItem(localStorageKey) === "true"
  )
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (wasClosed) return // Don't show if previously closed

    const timer = setTimeout(() => {
      setIsVisible(true)
    }, slideInAfter * 1000)

    return () => clearTimeout(timer)
  }, [wasClosed, slideInAfter])

  const handleClose = () => {
    setIsVisible(false)
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (!isVisible) {
      const timer = setTimeout(() => {
        localStorage.setItem(localStorageKey, "true")
        setWasClosed(true) // Prevent re-render
      }, 350)
      return () => clearTimeout(timer)
    }
  }, [isVisible, localStorageKey])

  if (wasClosed) return null

  const className = classNames(
    "relative w-[297px] h-[307px] shadow-lg transform transition-all duration-300",
    isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
  )

  return (
    <div className="fixed bottom-6 right-4 z-50">
      <div className={className}>
        <button
          onClick={handleClose}
          className="absolute -top-6 right-0 w-10 h-5 bg-slate-800 bg-opacity-70 text-white rounded-full flex items-center justify-center hover:bg-opacity-100 focus:outline-none transition-colors duration-150"
          aria-label="Close"
        >
          <XIcon className="h-[18px] w-auto" />
        </button>

        <div className="rounded-lg bg-white overflow-hidden">
          <div className="h-full overflow-auto">{children}</div>
        </div>
      </div>
    </div>
  )
}

export default PopUpNotification
